import {
  slate,
  AhaEditorUtils,
  AllPlugins,
  type AhaReferenceElement,
  type ReferenceAhaObject,
  type EditorPlugin,
} from '@aha-app/aha-editor-v2';
import { detectReferenceLink } from './utils';

const insertAhaReference = (
  editor: slate.Editor,
  reference: any,
  onInsertReference?: (reference: ReferenceAhaObject) => void
) => {
  if (!editor.selection) return;

  const props: Partial<AhaReferenceElement> = {};
  if (reference.class) props.reference_class = reference.class;
  if (reference.url) props.reference_url = reference.url;
  if (reference.id) props.reference_id = reference.id;
  if (reference.reference_num) props.reference_num = reference.reference_num;
  if (reference.name) props.reference_name = reference.name;
  if (reference.visibleFields)
    props.reference_visibleFields = reference.visibleFields;

  slate.Transforms.insertNodes(editor, {
    link_href: reference.linkUrl,
    type: 'link',
    children: [{ text: reference.linkUrl }],
  });

  AhaEditorUtils.Selection.moveAfterCurrentNode(editor);

  slate.Transforms.delete(editor, {
    distance: reference.linkUrl.length,
    unit: 'character',
    reverse: true,
  });

  // @ts-expect-error - we know props is a partial AhaReferenceElement
  slate.Transforms.insertNodes(editor, {
    ...props,
    type: 'aha-reference',
    children: [{ text: '' }],
  });

  const pathRef = slate.Editor.pathRef(
    editor,
    slate.Path.parent(editor.selection.anchor.path)
  );

  AhaEditorUtils.Selection.moveAfterCurrentNode(editor);

  if (onInsertReference) {
    onInsertReference(reference);
  }

  return pathRef;
};

interface PasteReferenceLinkPluginOptions {
  onInsertReference?: ((reference: ReferenceAhaObject) => void) | undefined;
}

const PasteReferenceLinkPlugin = ({
  onInsertReference,
}: PasteReferenceLinkPluginOptions = {}): EditorPlugin => {
  return {
    name: 'Paste reference link plugin',
    insertData: (editor, data) => {
      if (!editor.selection || !slate.Range.isCollapsed(editor.selection)) {
        return false;
      }

      const text = data.getData('text/plain');
      if (!text) return false;

      const ahaReference = detectReferenceLink(text);
      if (!ahaReference) return false;

      if (ahaReference instanceof Promise) {
        void ahaReference.then(async ({ reference, fetchData }) => {
          const pathRef = insertAhaReference(
            editor,
            reference,
            onInsertReference
          );
          const data = await fetchData();

          const path = pathRef?.unref();
          if (!path) return;

          slate.Transforms.setNodes(
            editor,
            {
              reference_name: data.name ?? undefined,
              reference_id: data.id,
            },
            { at: path }
          );
        });
      } else {
        const pathRef = insertAhaReference(
          editor,
          ahaReference,
          onInsertReference
        );
        pathRef?.unref();
      }

      return true;
    },
    dependencies: [
      AllPlugins.LinkSchemaPlugin,
      AllPlugins.LinkNormalizationPlugin,
    ],
  };
};

export default PasteReferenceLinkPlugin;
